import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    FormControlLabel,
    Checkbox,
    TextField,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    SelectChangeEvent,
    MenuItem as MuiMenuItem,
} from '@mui/material';
import {
    FaPlus,
    FaTimes,
    FaSave,
    FaTrash,
    FaArrowDown,
    FaArrowUp,
    FaUpload,
} from 'react-icons/fa';
import SelectSupplementDialog, { Supplement } from './SelectSupplementDialog';
import AllergenSelect from './Allergens';
import SectionTitle from '../Common/Title';
import SupplementsTable from './SupplementsTable';
import { SelectBaseDialog, SelectIngredientsDialog } from './SelectIngredientDialog';
import PricingSection from '../Bundles/PricingSection';
import Loader from '../Common/Loader';
import { uploadImage } from '../../services/imageUploadService';
import { 
    MenuItem, 
    PriceOption, 
    Ingredient, 
    IngredientReference, 
    SupplementReference,
    FoodCategory
} from '../../models/interfaces';

// Define local interfaces that extend or complement the global ones
interface LocalIngredient extends Ingredient {
    order: number;
}

interface BaseIngredientProps {
    baseIngredient: LocalIngredient | null;
    onChangeClick: () => void;
}

interface IngredientsTableProps {
    ingredients: LocalIngredient[];
    onMove: (ingredients: LocalIngredient[]) => void;
    onRemove: (id: string) => void;
    onAdd: () => void;
}

interface MenuItemFormProps {
    item?: MenuItem;
    foodCategories: FoodCategory[];
    supplements: Supplement[];
    ingredients: Ingredient[];
    onSave: (item: MenuItem) => Promise<void>;
    onClose: () => void;
}

// Extend the MenuItem interface to include the missing properties
interface ExtendedMenuItem extends MenuItem {
    description?: string;
    allergens?: string[];
    imageUrl?: string;
    image?: File | null;
}

const BaseIngredient: React.FC<BaseIngredientProps> = ({ baseIngredient, onChangeClick }) => {
    if (!baseIngredient) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onChangeClick}
                    startIcon={<FaPlus />}
                >
                    Choisir la base
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
            }}
        >
            <Box>
                <Typography variant="subtitle1">{baseIngredient.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    Base {baseIngredient.isOutOfStock ? '(Indisponible)' : '(Disponible)'}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={onChangeClick}
                size="small"
            >
                Changer
            </Button>
        </Box>
    );
};

const IngredientsTable: React.FC<IngredientsTableProps> = ({ ingredients, onMove, onRemove, onAdd }) => {
    const handleMoveUp = (index: number) => {
        if (index === 0) return;
        const newIngredients = [...ingredients];
        [newIngredients[index - 1], newIngredients[index]] = [newIngredients[index], newIngredients[index - 1]];
        onMove(newIngredients);
    };

    const handleMoveDown = (index: number) => {
        if (index === ingredients.length - 1) return;
        const newIngredients = [...ingredients];
        [newIngredients[index], newIngredients[index + 1]] = [newIngredients[index + 1], newIngredients[index]];
        onMove(newIngredients);
    };

    return (
        <Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width={80} align="center">#</TableCell>
                            <TableCell>Nom</TableCell>
                            <TableCell width={140} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ingredients.map((ingredient, index) => (
                            <TableRow key={ingredient.id || index}>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveUp(index)}
                                            disabled={index === 0}
                                            sx={{ p: 0.5 }}
                                        >
                                            <FaArrowUp size={12} />
                                        </IconButton>
                                        <Typography variant="body2" sx={{ my: 0.5 }}>
                                            {index + 1}
                                        </Typography>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveDown(index)}
                                            disabled={index === ingredients.length - 1}
                                            sx={{ p: 0.5 }}
                                        >
                                            <FaArrowDown size={12} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{ingredient.name}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        onClick={() => onRemove(ingredient.id)}
                                        size="small"
                                        color="error"
                                    >
                                        <FaTrash />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {ingredients.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Aucun ingrédient ajouté
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    startIcon={<FaPlus />}
                    onClick={onAdd}
                    variant="contained"
                    color="primary"
                    size="small"
                >
                    Ajouter un ingrédient
                </Button>
            </Box>
        </Box>
    );
};

const MenuItemForm: React.FC<MenuItemFormProps> = ({ item, foodCategories, supplements, ingredients, onSave, onClose }) => {
    const [formData, setFormData] = useState<ExtendedMenuItem>(() => {
        const defaultPrice: PriceOption = { type: 'default', vat: 5.5, priceTTC: 0, discountPriceTTC: null };
        const initialPrices = Array.isArray(item?.prices) && item.prices.length > 0
            ? item.prices.map(price => ({
                ...price,
                priceTTC: parseFloat(price.priceTTC.toString()) || 0,
                discountPriceTTC: price.discountPriceTTC != null ? parseFloat(price.discountPriceTTC.toString()) : null
            }))
            : [defaultPrice];

        return {
            id: item?.id || '',
            name: item?.name || '',
            categoryId: item?.categoryId || '',
            description: (item as any)?.description || '',
            isHidden: item?.isHidden || false,
            isOutOfStock: item?.isOutOfStock || false,
            preparationTime: item?.preparationTime || 0,
            complexityPoints: item?.complexityPoints || 0,
            prices: initialPrices,
            ingredients: item?.ingredients || [],
            supplements: item?.supplements || [],
            allergens: (item as any)?.allergens || [],
            imageUrl: (item as any)?.imageUrl || '',
            image: null,
        };
    });
    
    const [imagePreview, setImagePreview] = useState<string | null>((item as any)?.imageUrl || null);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isBaseDialogOpen, setBaseDialogOpen] = useState<boolean>(false);
    const [isIngredientsDialogOpen, setIngredientsDialogOpen] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        return () => {
            // Clean up any object URLs to avoid memory leaks
            if (imagePreview && !imagePreview.startsWith('http')) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        // TypeScript typeguard pour identifier un événement de checkbox
        const isCheckboxEvent = (
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
        ): e is React.ChangeEvent<HTMLInputElement> => {
            return 'checked' in e.target;
        };

        if (isCheckboxEvent(e)) {
            // C'est un événement de checkbox
            setFormData(prevData => ({
                ...prevData,
                [e.target.name]: e.target.checked
            }));
        } else {
            // Pour tous les autres types d'input
            setFormData(prevData => ({
                ...prevData,
                [e.target.name]: e.target.value
            }));
        }
    };

    // Convert Ingredient to LocalIngredient
    const toLocalIngredient = (ing: Ingredient, order: number): LocalIngredient => ({
        ...ing,
        order
    });

    // État pour la base
    const [baseIngredient, setBaseIngredient] = useState<LocalIngredient | null>(() => {
        if (!item?.base?.id) return null;
        
        const foundIngredient = ingredients.find(ing => ing.id === item.base?.id);
        if (!foundIngredient) return null;
        
        return toLocalIngredient(foundIngredient, 0);
    });

    // État pour les ingrédients standards
    const [standardIngredients, setStandardIngredients] = useState<LocalIngredient[]>(() => {
        if (!item?.ingredients?.length) return [];
        
        return item.ingredients
            .map((itemIngredient, index) => {
                const fullIngredient = ingredients.find(i => i.id === itemIngredient.id);
                if (!fullIngredient) return null;
                
                return toLocalIngredient(fullIngredient, itemIngredient.order || index);
            })
            .filter((ing): ing is LocalIngredient => ing !== null)
            .sort((a, b) => a.order - b.order);
    });

    // Gestionnaires pour la base
    const handleBaseSelect = (selectedBase: Ingredient | null) => {
        setBaseIngredient(selectedBase ? toLocalIngredient(selectedBase, 0) : null);
        setBaseDialogOpen(false);
    };

    // Gestionnaires pour les ingrédients standards
    const handleIngredientsSelect = (selectedIngredients: Ingredient[]) => {
        const currentLength = standardIngredients.length;
        const newIngredients = selectedIngredients.map((ingredient, index) => 
            toLocalIngredient(ingredient, currentLength + index)
        );

        setStandardIngredients([
            ...standardIngredients,
            ...newIngredients
        ]);
        setIngredientsDialogOpen(false);
    };

    const handleMoveStandardIngredients = (newIngredients: LocalIngredient[]) => {
        setStandardIngredients(newIngredients.map((ingredient, index) => ({
            ...ingredient,
            order: index
        })));
    };

    const handleRemoveStandardIngredient = (ingredientId: string) => {
        setStandardIngredients(prev =>
            prev.filter(ing => ing.id !== ingredientId)
                .map((ing, index) => ({ ...ing, order: index }))
        );
    };

    // Supplements
    const [itemSupplements, setItemSupplements] = useState<Supplement[]>(() => {
        if (!item?.supplements?.length) return [];
        
        return item.supplements
            .map((itemSupplement, index) => {
                const fullSupplement = supplements.find(s => s.id === itemSupplement.id);
                if (!fullSupplement) return null;

                return {
                    ...fullSupplement,
                    order: itemSupplement.order || index
                };
            })
            .filter((sup): sup is Supplement => sup !== null)
            .sort((a, b) => a.order - b.order);
    });

    const [isSelectSupplementDialogOpen, setIsSelectSupplementDialogOpen] = useState<boolean>(false);

    const handleOpenSelectSupplementDialog = () => {
        setIsSelectSupplementDialogOpen(true);
    };

    const handleMoveSupplements = (newSupplements: Supplement[]) => {
        setItemSupplements(newSupplements.map((supplement, index) => ({
            ...supplement,
            order: index
        })));
    };

    const handleCloseSelectSupplementDialog = () => {
        setIsSelectSupplementDialogOpen(false);
    };

    const handleSelectSupplement = (selectedSupplements: Supplement[]) => {
        const currentLength = itemSupplements.length;
        const newSupplements = selectedSupplements.map((supplement, index) => ({
            ...supplement,
            order: currentLength + index
        }));

        setItemSupplements([
            ...itemSupplements,
            ...newSupplements
        ]);
        setIsSelectSupplementDialogOpen(false);
    };

    const handleRemoveSupplement = (supplementId: string) => {
        const newSupplements = itemSupplements
            .filter(s => s.id !== supplementId)
            .map((supplement, index) => ({
                ...supplement,
                order: index
            }));
        setItemSupplements(newSupplements);
    };

    // Image handling
    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement> | { dataTransfer: { files: FileList } }) => {
        let file: File | null = null;
        
        // Handle both drag and drop and file input
        if ('dataTransfer' in e) {
            file = e.dataTransfer.files[0];
        } else if (e.target.files && e.target.files.length > 0) {
            file = e.target.files[0];
        }
        
        if (!file) return;
        
        setIsUploading(true);
        try {
            // Create a temporary URL for preview
            const previewURL = URL.createObjectURL(file);
            setImagePreview(previewURL);

            // Upload to server
            const path = `menu-items/${Date.now()}_${file.name}`;
            const downloadURL = await uploadImage(file, path);

            setFormData(prevData => ({
                ...prevData,
                imageUrl: downloadURL,
                // Ne pas stocker l'objet File dans formData
            }));
        } catch (error) {
            console.error("Error uploading image: ", error);
            // Handle error (e.g., show a message to the user)
        } finally {
            setIsUploading(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleImageUpload({ dataTransfer: e.dataTransfer });
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Allergen handling
    const handleAllergenChange = (newAllergens: string[]) => {
        setFormData(prev => ({
            ...prev,
            allergens: newAllergens
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            // Prepare base ingredient reference
            const base = baseIngredient ? {
                id: baseIngredient.id,
                order: 0
            } : undefined;

            // Prepare ingredients data for the API
            const ingredientsData: IngredientReference[] = standardIngredients.map((ingredient, index) => ({
                id: ingredient.id,
                order: index + 1
            }));

            // Prepare supplements data for the API
            const supplementsData: SupplementReference[] = itemSupplements
                .sort((a, b) => a.order - b.order)
                .map((supplement, index) => ({
                    id: supplement.id,
                    order: index
                }));

            // Supprimer la propriété image qui contient l'objet File non-sérialisable
            const { image, ...formDataWithoutFile } = formData;

            // Create the final menu item to save
            const finalMenuItem: ExtendedMenuItem = {
                ...formDataWithoutFile,
                base,
                ingredients: ingredientsData,
                supplements: supplementsData,
            };

            await onSave(finalMenuItem as MenuItem);
            onClose();
        } catch (error) {
            console.error("Error saving menu item: ", error);
        } finally {
            setIsSaving(false);
        }
    };
    
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            position: 'relative',
            bgcolor: 'background.default',
        }}>
            <Box sx={{
                flexGrow: 1,
                overflowY: 'auto',
                p: 3,
                pb: 10
            }}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">Produit</Typography>
                        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
                            <FaTimes />
                        </IconButton>
                    </Box>

                    {item && item.id && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                ID : <span style={{ userSelect: 'all', fontFamily: 'monospace' }}>{item.id}</span>
                            </Typography>
                        </Box>
                    )}

                    <SectionTitle>Informations générales</SectionTitle>
                    <TextField
                        fullWidth
                        label="Nom"
                        name="itemName"
                        value={formData.name}
                        onChange={(e) => {
                            // Mapper manuellement à la propriété "name" de formData
                            setFormData(prevData => ({
                                ...prevData,
                                name: e.target.value
                            }));
                        }}
                                            margin="normal"
                        variant="outlined"
                    />

                    <FormControl fullWidth margin="normal" variant="outlined">
                        <InputLabel>Catégories</InputLabel>
                        <Select
                            name="categoryId"
                            label="Catégories"
                            value={formData.categoryId || ''}
                            onChange={(e) => {
                                setFormData(prevData => ({ ...prevData, categoryId: e.target.value }));
                            }}
                        >
                            {foodCategories.map(category => (
                                <MuiMenuItem key={category.id} value={category.id}>{category.name}</MuiMenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <SectionTitle>Disponibilité</SectionTitle>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={!formData.isOutOfStock} 
                                onChange={(e) => setFormData(prev => ({ ...prev, isOutOfStock: !e.target.checked }))}
                            />
                        }
                        label="Disponible à la vente"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={!formData.isHidden} 
                                onChange={(e) => setFormData(prev => ({ ...prev, isHidden: !e.target.checked }))}
                            />
                        }
                        label="Visible sur la boutique"
                    />

                    <SectionTitle>Description</SectionTitle>
                    <TextField
                        fullWidth
                        label="Description du produit"
                        name="description"
                        multiline
                        rows={4}
                        value={formData.description || ''}
                        onChange={handleChange}
                        margin="normal"
                        variant="outlined"
                    />


                    <SectionTitle>Base</SectionTitle>
                    <BaseIngredient
                        baseIngredient={baseIngredient}
                        onChangeClick={() => setBaseDialogOpen(true)}
                    />

                    <SectionTitle>Ingrédients</SectionTitle>
                    <IngredientsTable
                        ingredients={standardIngredients}
                        onMove={handleMoveStandardIngredients}
                        onRemove={handleRemoveStandardIngredient}
                        onAdd={() => setIngredientsDialogOpen(true)}
                    />

                    <SectionTitle>Image</SectionTitle>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Taille recommandée : 1024 x 1024 px, zone centrale toujours visible : 1024 x 700 px, 2 Mo maximum.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                            sx={{
                                width: 150,
                                height: 150,
                                border: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                overflow: 'hidden',
                                borderRadius: 2
                            }}
                        >
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    Aucune image
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 200,
                                height: 150,
                                border: '2px dashed',
                                borderColor: 'primary.main',
                                borderRadius: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                        >
                            {isUploading ? (
                                <Loader />
                            ) : (
                                <>
                                    <FaUpload size={24} color="#1976d2" />
                                    <Typography variant="body2" color="primary" align="center" sx={{ mt: 1 }}>
                                        Glissez un fichier ici ou <span style={{ textDecoration: 'underline' }}>sélectionnez un fichier</span>
                                    </Typography>
                                </>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".png,.jpg,.jpeg,.gif,.svg"
                                onChange={handleImageUpload}
                            />
                        </Box>
                    </Box>

                    <SectionTitle>Allergènes</SectionTitle>
                    <AllergenSelect
                        value={formData.allergens || []}
                        onChange={handleAllergenChange}
                    />

                    <SectionTitle>Prix</SectionTitle>
                    <PricingSection
                        formData={formData}
                        setFormData={setFormData}
                    />

                    <SectionTitle>Suppléments</SectionTitle>
                    <SupplementsTable
                        supplements={itemSupplements}
                        onMove={handleMoveSupplements}
                        onRemove={handleRemoveSupplement}
                        onAdd={handleOpenSelectSupplementDialog}
                    />
                </Box>
                {isBaseDialogOpen && (
                    <SelectBaseDialog
                        open={isBaseDialogOpen}
                        onClose={() => setBaseDialogOpen(false)}
                        ingredients={ingredients}
                        currentBaseId={baseIngredient?.id ?? null}
                        onSelect={handleBaseSelect}
                    />
                )}

                {isIngredientsDialogOpen && (
                    <SelectIngredientsDialog
                        open={isIngredientsDialogOpen}
                        onClose={() => setIngredientsDialogOpen(false)}
                        ingredients={ingredients}
                        selectedIngredients={standardIngredients}
                        onSelect={handleIngredientsSelect}
                    />
                )}

                {isSelectSupplementDialogOpen && (
                    <SelectSupplementDialog
                        open={isSelectSupplementDialogOpen}
                        onClose={handleCloseSelectSupplementDialog}
                        onSelect={handleSelectSupplement}
                        supplements={supplements}
                        selectedSupplements={itemSupplements}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'sticky',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: 'background.paper',
                boxShadow: '0px -2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                zIndex: 1
            }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving || isUploading}
                    startIcon={<FaTimes />}
                    sx={{
                        minWidth: '120px',
                        color: 'text.secondary',
                        borderColor: 'text.secondary',
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        }
                    }}
                >
                    Annuler
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {(isSaving || isUploading) && (
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();
                            // Call handleSubmit with a synthetic form event
                            handleSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>);
                        }}
                        disabled={isSaving || isUploading}
                        startIcon={<FaSave />}
                        sx={{
                            minWidth: '120px',
                            backgroundColor: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                            }
                        }}
                    >
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default MenuItemForm;